import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Home, User as UserIcon, Phone, MapPin, ImagePlus, X } from "lucide-react";
import api, { formatErr, fileUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { BRAND_COLORS } from "../lib/bookingStatus";

export default function CustomerProfileSetup() {
  const { lang } = useI18n();
  const de = lang === "de";
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    phone: user?.phone || "",
    welcome_name: user?.welcome_name || "",
    property_address: "",
    property_city: "",
    hero_image_url: user?.hero_image_url || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(de ? "Bitte eine Bilddatei wählen." : "Please pick an image file.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error(de ? "Bild ist zu groß (max. 8 MB)." : "Image too large (max. 8 MB).");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, hero_image_url: data.id }));
      toast.success(de ? "Startbild hochgeladen." : "Hero image uploaded.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.phone || !form.welcome_name || !form.property_address) {
      toast.error(de ? "Bitte alle Pflichtfelder ausfüllen." : "Please fill in all required fields.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/me/profile-setup", form);
      toast.success(de ? "Profil erfolgreich angelegt." : "Profile created successfully.");
      await refresh?.();
      nav("/app", { replace: true });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-ivory flex items-center justify-center py-12 px-6" data-testid="customer-profile-setup">
      <div className="w-full max-w-2xl">
        <div className="mb-10 text-center">
          <div className="label-caps mb-3" style={{ color: BRAND_COLORS.gold }}>
            {de ? "Willkommen bei Weissblom" : "Welcome to Weissblom"}
          </div>
          <h1 className="font-display text-4xl md:text-5xl text-navy leading-tight mb-3">
            {de ? <>Nur einen Moment, <span className="italic">bitte</span></> : <>Just a moment, <span className="italic">please</span></>}
          </h1>
          <p className="text-[14px] text-navy-dark/70 max-w-md mx-auto leading-relaxed">
            {de
              ? "Damit Ihr Concierge-Team Sie persönlich betreuen kann, benötigen wir noch ein paar Angaben zu Ihnen und Ihrer Immobilie."
              : "So that your concierge team can look after you personally, we need a few details about you and your property."}
          </p>
        </div>

        <form onSubmit={submit} className="bg-white border border-navy/10 p-8 md:p-10 space-y-8" data-testid="profile-setup-form">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <UserIcon className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
              <div className="label-caps">{de ? "Ihr Name" : "Your name"}</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                data-testid="setup-first-name"
                value={form.first_name}
                onChange={upd("first_name")}
                placeholder={de ? "Vorname" : "First name"}
                required
                className="w-full border border-navy/15 px-4 py-3 text-[14px] text-navy-dark focus:border-gold focus:outline-none"
              />
              <input
                data-testid="setup-last-name"
                value={form.last_name}
                onChange={upd("last_name")}
                placeholder={de ? "Nachname" : "Last name"}
                required
                className="w-full border border-navy/15 px-4 py-3 text-[14px] text-navy-dark focus:border-gold focus:outline-none"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <Phone className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
              <div className="label-caps">{de ? "Telefon" : "Phone"}</div>
            </div>
            <input
              data-testid="setup-phone"
              value={form.phone}
              onChange={upd("phone")}
              placeholder={de ? "+49 …" : "+49 …"}
              required
              type="tel"
              className="w-full border border-navy/15 px-4 py-3 text-[14px] text-navy-dark focus:border-gold focus:outline-none"
            />
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
              <div className="label-caps">{de ? "Adresse der Immobilie" : "Property address"}</div>
            </div>
            <input
              data-testid="setup-property-address"
              value={form.property_address}
              onChange={upd("property_address")}
              placeholder={de ? "Straße und Hausnummer" : "Street and house number"}
              required
              className="w-full border border-navy/15 px-4 py-3 text-[14px] text-navy-dark focus:border-gold focus:outline-none mb-3"
            />
            <input
              data-testid="setup-property-city"
              value={form.property_city}
              onChange={upd("property_city")}
              placeholder={de ? "Ort (optional)" : "City (optional)"}
              className="w-full border border-navy/15 px-4 py-3 text-[14px] text-navy-dark focus:border-gold focus:outline-none"
            />
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <Home className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
              <div className="label-caps">{de ? "Begrüßungsname" : "Welcome name"}</div>
            </div>
            <input
              data-testid="setup-welcome-name"
              value={form.welcome_name}
              onChange={upd("welcome_name")}
              placeholder={de
                ? "z. B. Villa Sørensen, Chalet Bergblick, Södergården …"
                : "e.g. Villa Sørensen, Chalet Bergblick, Södergården …"}
              required
              className="w-full border border-navy/15 px-4 py-3 text-[14px] text-navy-dark focus:border-gold focus:outline-none"
            />
            <p className="text-[11.5px] text-navy-dark/55 mt-2 italic leading-relaxed">
              {de
                ? "Dieser Name wird prominent auf Ihrem Home-Screen angezeigt — wählen Sie einen Namen, der Ihre Immobilie liebevoll benennt."
                : "This name appears prominently on your home screen — pick something that names your home warmly."}
            </p>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <ImagePlus className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
              <div className="label-caps">{de ? "Ihr Startbild (optional)" : "Your hero image (optional)"}</div>
            </div>
            {form.hero_image_url ? (
              <div className="relative border border-navy/15" data-testid="hero-preview">
                <img src={fileUrl(form.hero_image_url)} alt={de ? "Startbild" : "Hero image"} className="w-full aspect-[16/9] object-cover" />
                <button
                  type="button"
                  data-testid="hero-remove-btn"
                  onClick={() => setForm({ ...form, hero_image_url: "" })}
                  className="absolute top-3 right-3 bg-white/95 hover:bg-white text-navy p-1.5 border border-navy/20"
                  aria-label={de ? "Bild entfernen" : "Remove image"}
                >
                  <X className="w-4 h-4" strokeWidth={1.6} />
                </button>
              </div>
            ) : (
              <label
                htmlFor="hero-upload"
                data-testid="hero-upload-label"
                className="flex flex-col items-center justify-center gap-3 border border-dashed border-navy/25 hover:border-gold px-6 py-10 cursor-pointer transition-colors bg-ivory/40"
              >
                <ImagePlus className="w-8 h-8" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.2} />
                <div className="label-caps">
                  {uploading ? (de ? "Wird hochgeladen…" : "Uploading…") : (de ? "Bild auswählen" : "Pick image")}
                </div>
                <p className="text-[11.5px] text-navy-dark/55 italic max-w-xs text-center">
                  {de
                    ? "Ein Foto Ihrer Immobilie oder eines Lieblingsdetails — verleiht Ihrer App den persönlichen „Villa-Look\u201C."
                    : "A photo of your property or a favourite detail — gives your app that personal villa look."}
                </p>
                <input
                  id="hero-upload"
                  data-testid="hero-file-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={onFile}
                  disabled={uploading}
                />
              </label>
            )}
          </div>

          <div className="pt-4 border-t border-navy/10">
            <button
              type="submit"
              data-testid="setup-submit-btn"
              disabled={saving}
              className="w-full bg-navy text-ivory py-4 text-[12px] tracking-[0.18em] uppercase hover:bg-navy-dark transition-colors disabled:opacity-60"
              style={{ background: BRAND_COLORS.navy }}
            >
              {saving
                ? (de ? "Wird gespeichert…" : "Saving…")
                : (de ? "Profil abschließen" : "Complete profile")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
